import { AnimatePresence } from 'framer-motion';
import React, { ReactNode, useState } from 'react';

import RichText from 'components/RichText/RichText';
import {
  triggerDefaultCursor,
  triggerScaleUpCursor,
} from 'utils/cursorController';

import { AccordionVariants } from './Accordion.animations';

import * as S from './Accordion.styles';

export interface PanelProps {
  delay?: number;
  heading: string;
  content: ReactNode;
}

export interface AccordionProps {
  items: PanelProps[];
}

const Panel: React.FunctionComponent<PanelProps> = ({
  delay,
  heading,
  content,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleToggle = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  return (
    <S.PanelWrapper
      initial="initial"
      animate="animate"
      custom={{ delay }}
      variants={AccordionVariants.wrapper}
    >
      <S.Toggle
        title={heading}
        isOpen={isOpen}
        onClick={handleToggle}
        onMouseEnter={triggerScaleUpCursor}
        onMouseLeave={triggerDefaultCursor}
      >
        <RichText
          as="h4"
          size="heading4"
          richText={heading}
          defaultFormatting
        />
      </S.Toggle>

      <AnimatePresence initial={false}>
        {isOpen && (
          <S.Content
            initial="initial"
            animate="animate"
            exit="initial"
            variants={AccordionVariants.innerContent}
          >
            {content}
          </S.Content>
        )}
      </AnimatePresence>
    </S.PanelWrapper>
  );
};

const defaultProps: Partial<AccordionProps> = {
  items: [],
};

const Accordion: React.FunctionComponent<AccordionProps> = ({
  items = [],
  ...rest
}) => (
  <S.Wrapper {...rest}>
    <S.List>
      {items.map(({ heading, content }, index) => (
        <Panel
          key={heading}
          delay={index * 0.2}
          heading={heading}
          content={content}
        />
      ))}
    </S.List>
  </S.Wrapper>
);

Accordion.defaultProps = defaultProps;

export default Accordion;
